import React, { useState, useEffect } from 'react';
import NavBar from '../components/NavBar';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import { useSidebar } from '../contexts/SidebarContext';
import axios from 'axios';
import { supabase } from '../supabaseClient';
import { API_URL } from '../globals';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText } from 'lucide-react';
import TestCaseDialog from '../components/TestCaseDialog';

// PageListItem component to display individual page items
const PageListItem = ({ page, onPageClick }) => {
  return (
    <Box
      onClick={() => onPageClick(page)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 2,
        borderRadius: '6px',
        backgroundColor: '#1a1a1a',
        border: '1px solid #2a2a2a',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: '#1e1e1e',
          borderColor: '#fff',
        }
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            color: '#fff',
            fontWeight: 500,
            fontSize: '0.875rem',
            mb: 0.25,
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {page.name?.replace(/_/g, ' ') || 'Unnamed Page'}
        </Typography>
        
        <Typography
          sx={{
            color: '#888',
            fontSize: '0.75rem',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {page.url_format || 'No URL pattern'}
        </Typography>
      </Box>
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: 2 }}>
        {page.entry_page && (
          <Typography
            sx={{
              color: '#66bb6a',
              fontSize: '0.65rem',
              fontWeight: 500,
              px: 1,
              py: 0.25,
              borderRadius: '3px',
              backgroundColor: 'rgba(102, 187, 106, 0.1)',
              border: '1px solid rgba(102, 187, 106, 0.3)',
            }}
          >
            Entry
          </Typography>
        )}
        <Typography
          sx={{
            color: '#90caf9',
            fontSize: '0.65rem',
            fontWeight: 500,
            px: 1,
            py: 0.25,
            borderRadius: '3px',
            backgroundColor: 'rgba(144, 202, 249, 0.1)',
            border: '1px solid rgba(144, 202, 249, 0.3)',
          }}
        >
          {page.test_case_count}
        </Typography>
      </Box>
    </Box>
  );
};

function Pages({ hideNavBar = false, studioId: propStudioId }) {
  const { isOpen, isHovered } = useSidebar();
  const navbarWidth = isOpen || isHovered ? 240 : 64;
  const { studioId: paramStudioId } = useParams();
  const studioId = propStudioId || paramStudioId;
  const navigate = useNavigate();

  const [pages, setPages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalTestCount, setTotalTestCount] = useState(0);
  const [selectedPage, setSelectedPage] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const fetchPages = async () => {
    if (!studioId) {
        setError("Studio ID is missing.");
        setIsLoading(false);
        return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const session = await supabase.auth.getSession();
      if (!session?.data?.session?.access_token) {
        throw new Error("Not authenticated");
      }
      const authToken = session.data.session.access_token;
      const headers = {
        Authorization: `Bearer ${authToken}`,
      };

      // Fetch pages for the studio
      const response = await axios.get(`${API_URL}/test/pages?studio_id=${studioId}`, { headers });

      if (response.data) {
        // Include pages that either have test cases or are entry pages
        const filteredAndSortedPages = [...response.data]
          .filter(page => page.test_case_count > 0 || page.entry_page)
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        
        // Calculate total test count from all pages (including filtered ones)
        const total = response.data.reduce((sum, page) => sum + (page.test_case_count || 0), 0);
        setTotalTestCount(total);
        setPages(filteredAndSortedPages);
      } else {
        setPages([]);
        setTotalTestCount(0);
      }
    } catch (err) {
      console.error("Error fetching pages:", err);
      setError(err.message || "Failed to fetch pages");
      setPages([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPages();
  }, [studioId]);

  const handlePageClick = (page) => {
    setSelectedPage(page);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedPage(null);
  };

  return (
    <>
      {!hideNavBar && <NavBar />}

      <Box
        component="main"
        sx={{
          minHeight: hideNavBar ? 'auto' : '100vh',
          marginLeft: hideNavBar ? 0 : `${navbarWidth}px`,
          transition: 'margin-left 0.2s',
          width: hideNavBar ? '100%' : `calc(100% - ${navbarWidth}px)`,
          p: hideNavBar ? 2 : 3,
          backgroundColor: '#121212',
          boxSizing: 'border-box'
        }}
      >
        {!hideNavBar && (
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#fff',
                  }}
                >
                  Pages
                </Typography>
                <Typography
                  sx={{
                    color: '#888',
                    fontSize: '1.2rem',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  •
                </Typography>
                <Typography
                  sx={{
                    color: '#90caf9',
                    fontSize: '1.1rem',
                    fontWeight: 500,
                  }}
                >
                  {totalTestCount} Total Tests
                </Typography>
              </Box>
              
              <Button
                variant="contained"
                onClick={() => navigate(`/create-tests/${studioId}`)}
                sx={{
                  backgroundColor: '#1e1e1e',
                  color: '#e0e0e0',
                  border: '1px solid #383838',
                  borderRadius: '12px',
                  px: 3,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '0.9rem',
                  '&:hover': {
                    backgroundColor: '#2a2a2a',
                    borderColor: '#555',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.3)',
                  },
                  transition: 'all 0.3s ease-in-out',
                }}
              >
                + Add New Tests
              </Button>
            </Box>
            <Typography sx={{ color: '#888', fontSize: '0.875rem' }}>
              List of pages available in this studio.
            </Typography>
          </Box>
        )}
        
        {hideNavBar && totalTestCount > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                color: '#888',
                fontSize: '0.75rem',
                fontWeight: 500,
              }}
            >
              {pages.length} pages • {totalTestCount} tests
            </Typography>
          </Box>
        )}

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
            <CircularProgress sx={{ color: '#fff' }} />
          </Box>
        )}

        {error && (
          <Typography sx={{ color: 'error.main', textAlign: 'center', minHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            Error loading pages: {error}
          </Typography>
        )}

        {!isLoading && !error && (
          <Box sx={{ width: '100%' }}>
            {pages.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: hideNavBar ? 1 : 1.5 }}>
                {pages.map((page) => (
                  <PageListItem 
                    key={page._id || page.page_id || Math.random()}
                    page={page} 
                    onPageClick={handlePageClick} 
                  />
                ))}
              </Box>
            ) : (
              <Box sx={{
                p: hideNavBar ? 3 : 6,
                textAlign: 'center',
                borderRadius: '8px',
                backgroundColor: hideNavBar ? '#1a1a1a' : '#1e1e1e',
                border: '1px solid #2a2a2a',
                minHeight: hideNavBar ? '150px' : '300px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Box sx={{
                  mb: 2,
                  width: hideNavBar ? 48 : 64,
                  height: hideNavBar ? 48 : 64,
                  borderRadius: '8px',
                  backgroundColor: '#2a2a2a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <FileText size={hideNavBar ? 24 : 32} style={{ color: '#90caf9' }} />
                </Box>
                
                <Typography
                  sx={{
                    color: '#fff',
                    fontWeight: 500,
                    fontSize: hideNavBar ? '0.875rem' : '1rem',
                    mb: 1,
                  }}
                >
                  No test pages found
                </Typography>
                
                <Typography sx={{ 
                  color: '#888', 
                  fontSize: hideNavBar ? '0.75rem' : '0.875rem',
                  maxWidth: hideNavBar ? '250px' : '300px',
                }}>
                  {hideNavBar 
                    ? 'Create tests to discover pages in this studio'
                    : 'Create your first test to automatically discover and map pages'
                  }
                </Typography>
                
                {!hideNavBar && (
                  <Button
                    variant="contained"
                    onClick={() => navigate(`/create-tests/${studioId}`)}
                    sx={{
                      mt: 3,
                      backgroundColor: '#1e1e1e',
                      color: '#e0e0e0',
                      border: '1px solid #383838',
                      borderRadius: '8px',
                      px: 3,
                      py: 1.5,
                      fontWeight: 500,
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      '&:hover': {
                        backgroundColor: '#2a2a2a',
                        borderColor: '#4a90e2',
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    + Create Your First Test
                  </Button>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>

      <TestCaseDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        page={selectedPage}
        onDeleted={fetchPages}
      />
    </>
  );
}

export default Pages; 